import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaFileUpload, FaEye } from 'react-icons/fa';
import { IMAGE_URL } from '../../../Api/commonAPI';

const SectionG = ({ formData: parentFormData, onDataChange }) => {
    // Initialize documents from parentFormData
    const initialDocuments = parentFormData && Object.keys(parentFormData).length > 0
        ? parentFormData
        : {
            AadharCardF: null,
            AadharCardB: null,
            PRANCard: null,
            PANCard: null,
            Passbook: null
        };

    const [documents, setDocuments] = useState(initialDocuments);
    const [viewImage, setViewImage] = useState(null);
    const [viewImageName, setViewImageName] = useState('');
    
    // Refs to prevent circular updates
    const lastParentDataRef = useRef(JSON.stringify(parentFormData || {}));
    const isUpdatingFromParentRef = useRef(false);
    const lastSentToParentRef = useRef(JSON.stringify(documents));

    const documentFields = [
        { key: 'AadharCardF', label: 'Aadhar Card (Front Side)', required: true },
        { key: 'AadharCardB', label: 'Aadhar Card (Back Side)', required: true },
        { key: 'PRANCard', label: 'PRAN Card', required: true },
        { key: 'PANCard', label: 'Pan Card', required: true },
        { key: 'Passbook', label: 'Cancel Check Image or Passbook Photo', required: true }
    ];

    // Update parent when documents change
    useEffect(() => {
        if (isUpdatingFromParentRef.current) {
            return;
        }

        const currentData = JSON.stringify(documents);
        if (currentData !== lastSentToParentRef.current) {
            lastSentToParentRef.current = currentData;
            
            if (onDataChange) {
                onDataChange(documents);
            }
        }
    }, [documents, onDataChange]);

    // Update from parent data (API response)
    useEffect(() => {
        if (!parentFormData) {
            return;
        }

        const currentParentData = JSON.stringify(parentFormData);
        if (currentParentData === lastParentDataRef.current) {
            return;
        }

        const currentDocuments = JSON.stringify(documents);
        const newDocuments = JSON.stringify(parentFormData);
        
        if (currentDocuments !== newDocuments) {
            isUpdatingFromParentRef.current = true;
            setDocuments(parentFormData);
            lastParentDataRef.current = currentParentData;
            
            setTimeout(() => {
                isUpdatingFromParentRef.current = false;
            }, 100);
        }
    }, [parentFormData]);

    const handleFileChange = (key, event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
            if (!allowedTypes.includes(file.type)) {
                alert('Please upload only JPG, PNG or PDF files');
                return;
            }

            // Validate file size (max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                alert('File size should not exceed 5MB');
                return;
            }

            setDocuments(prev => ({
                ...prev,
                [key]: file
            }));
        }
    };

    const handleRemoveFile = (key) => {
        setDocuments(prev => ({
            ...prev,
            [key]: null
        }));
        // Reset file input
        const input = document.getElementById(`file-${key}`);
        if (input) input.value = '';
    };

    const handleViewImage = (url, fileName) => {
        if (url) {
            setViewImage(url);
            setViewImageName(fileName || 'Document');
        }
    };

    const handleCloseImageView = () => {
        setViewImage(null);
        setViewImageName('');
    };

    // Helper function to check if document is a URL or File object
    const getDocumentUrl = (doc) => {
        if (!doc) return null;
        if (typeof doc === 'string') {
            // It's a URL
            return doc.startsWith('http') ? doc : `${IMAGE_URL}${doc}`;
        }
        if (doc instanceof File) {
            // It's a File object
            return URL.createObjectURL(doc);
        }
        return null;
    };

    // Helper function to check if document is an image
    const isImage = (doc) => {
        if (!doc) return false;
        if (typeof doc === 'string') {
            // URL - check extension
            return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(doc);
        }
        if (doc instanceof File) {
            return doc.type.startsWith('image/');
        }
        return false;
    };

    // Helper function to get document name
    const getDocumentName = (doc, fieldKey) => {
        if (!doc) return '';
        if (doc instanceof File) {
            return doc.name;
        }
        if (typeof doc === 'string') {
            // Extract filename from URL
            const urlParts = doc.split('/');
            return urlParts[urlParts.length - 1] || documentFields.find(f => f.key === fieldKey)?.label || 'Document';
        }
        return '';
    };

    // Helper function to get document size
    const getDocumentSize = (doc) => {
        if (!doc) return '';
        if (doc instanceof File) {
            return `${(doc.size / 1024).toFixed(2)} KB`;
        }
        return '';
    };

    const isAllFilesUploaded = () => {
        return documentFields.every(field => field.required ? documents[field.key] : true);
    };

    return (
        <div className="section-container">
            <div className="card shadow-sm">
                <div className="card-header" style={{ background: '#0d6efd', color: 'white', padding: '1rem' }}>
                    <h5 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                        Attach Documents
                    </h5>
                </div>
                <div className="card-body p-4">
                    <div className="row g-4">
                        {documentFields.map((field) => (
                            <div key={field.key} className="col-12">
                                <label className="form-label" style={{ fontSize: '0.9rem', fontWeight: '500', color: '#333' }}>
                                    {field.label}
                                    {field.required && <span className="text-danger ms-1">*</span>}
                                </label>
                                
                                {documents[field.key] ? (
                                    // File uploaded or URL available - show preview
                                    <div className="d-flex align-items-center p-3 rounded" style={{ 
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6'
                                    }}>
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center">
                                                <div 
                                                    style={{ 
                                                        width: '50px',
                                                        height: '50px',
                                                        backgroundColor: '#e7f1ff',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        marginRight: '1rem',
                                                        cursor: isImage(documents[field.key]) ? 'pointer' : 'default',
                                                        overflow: 'hidden'
                                                    }}
                                                    onClick={() => isImage(documents[field.key]) && handleViewImage(getDocumentUrl(documents[field.key]), getDocumentName(documents[field.key], field.key))}
                                                    title={isImage(documents[field.key]) ? 'Click to view full size' : ''}
                                                >
                                                    {isImage(documents[field.key]) ? (
                                                        <img 
                                                            src={getDocumentUrl(documents[field.key])}
                                                            alt="preview"
                                                            style={{ 
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                borderRadius: '8px'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.parentElement.innerHTML = '<i class="fas fa-file-pdf" style="font-size: 1.5rem; color: #dc3545;"></i>';
                                                            }}
                                                        />
                                                    ) : (
                                                        <i className="fas fa-file-pdf" style={{ fontSize: '1.5rem', color: '#dc3545' }}></i>
                                                    )}
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#333' }}>
                                                        {getDocumentName(documents[field.key], field.key)}
                                                    </div>
                                                    {getDocumentSize(documents[field.key]) && (
                                                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                                            {getDocumentSize(documents[field.key])}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            {isImage(documents[field.key]) && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm"
                                                    onClick={() => handleViewImage(getDocumentUrl(documents[field.key]), getDocumentName(documents[field.key], field.key))}
                                                    style={{ 
                                                        padding: '0.25rem 0.5rem',
                                                        backgroundColor: 'transparent',
                                                        border: 'none',
                                                        color: '#0d6efd'
                                                    }}
                                                    title="View image"
                                                >
                                                    <FaEye style={{ fontSize: '1.2rem' }} />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-sm"
                                                onClick={() => handleRemoveFile(field.key)}
                                                style={{ 
                                                    padding: '0.25rem 0.5rem',
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    color: '#dc3545'
                                                }}
                                                title="Remove file"
                                            >
                                                <FaTimes style={{ fontSize: '1.2rem' }} />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    // No file uploaded - show upload button
                                    <div>
                                        <input
                                            type="file"
                                            id={`file-${field.key}`}
                                            accept="image/jpeg,image/jpg,image/png,application/pdf"
                                            onChange={(e) => handleFileChange(field.key, e)}
                                            style={{ display: 'none' }}
                                        />
                                        <label
                                            htmlFor={`file-${field.key}`}
                                            className="d-flex align-items-center justify-content-center p-4 rounded"
                                            style={{
                                                border: '2px dashed #dee2e6',
                                                backgroundColor: '#f8f9fa',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s',
                                                marginBottom: 0
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.borderColor = '#0d6efd';
                                                e.currentTarget.style.backgroundColor = '#e7f1ff';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.borderColor = '#dee2e6';
                                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            }}
                                        >
                                            <div className="text-center">
                                                <FaFileUpload style={{ fontSize: '2rem', color: '#0d6efd', marginBottom: '0.5rem' }} />
                                                <div style={{ fontSize: '0.9rem', color: '#0d6efd', fontWeight: '500' }}>
                                                    Upload file
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                                                    JPG, PNG or PDF (Max 5MB)
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Image View Modal */}
            {viewImage && (
                <>
                    <div
                        className="modal-backdrop fade show"
                        style={{ zIndex: 10050, backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
                        onClick={handleCloseImageView}
                    ></div>
                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        style={{ zIndex: 10055 }}
                        onClick={handleCloseImageView}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '90vw' }}>
                            <div className="modal-content" style={{ opacity: 1, backgroundColor: 'transparent', border: 'none' }}>
                                <div className="modal-header" style={{ border: 'none', justifyContent: 'flex-end', padding: '0.5rem' }}>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={handleCloseImageView}
                                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '50%', padding: '0.5rem' }}
                                    ></button>
                                </div>
                                <div className="modal-body" style={{ padding: '1rem', textAlign: 'center' }}>
                                    <img 
                                        src={viewImage} 
                                        alt={viewImageName}
                                        style={{ 
                                            maxWidth: '100%',
                                            maxHeight: '80vh',
                                            objectFit: 'contain',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = '<div style="color: white; padding: 2rem;">Image not available</div>';
                                        }}
                                    />
                                    <div style={{ color: 'white', marginTop: '1rem', fontSize: '0.9rem', fontWeight: '500' }}>
                                        {viewImageName}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SectionG;

